import React, { useState, useRef, useEffect } from "react";
import {
  Text,
  Title,
  Button,
  TextInput,
  ActionIcon,
  Group,
  Menu,
  Card,
  Space,
  Grid,
  Center,
  FileButton,
  Box,
  UnstyledButton,
  Image,
  Avatar,
  Input,
  Loader,
  Table,
  Checkbox,
  ScrollArea,
} from "@mantine/core";

import { useForceUpdate, useHotkeys, randomId } from "@mantine/hooks";
import { showNotification } from "@mantine/notifications";
import moment from "moment";
import {
  IconTrash,
  IconCheck,
  IconSettings,
  IconExclamationMark,
  IconMessageCircle,
  IconCoin,
  IconDownload,
  IconDotsVertical,
  IconList,
  IconGridPattern,
  IconPencil,
  IconLayoutGrid,
  IconRefresh,
  IconChevronUp,
  IconChevronDown,
  IconSelector,
  IconExternalLink,
  IconCircleCheck,
  IconSearch,
  IconX,
} from "@tabler/icons";
import { useTranslation } from "react-i18next";
import { Dropzone, DropzoneProps, IMAGE_MIME_TYPE } from "@mantine/dropzone";
import ReactJson from "react-json-view";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import _ from "lodash";
import {
  faFileImage,
  faFileVideo,
  faFileZipper,
  faFileCsv,
  faFilePdf,
  faFileWord,
  faFileExcel,
  faFilePowerpoint,
  faFile,
} from "@fortawesome/free-solid-svg-icons";
import { useServerApi } from "../hooks/userServerApi";
import { useDateRender } from "../hooks/useDateRender";
import { useClickOutside } from "@mantine/hooks";
import generatePdfThumbnails from "pdf-thumbnails-generator";
import DataTable from "./dataTable";
import { useAuthUser } from "react-auth-kit";
import { saveBlobDownload } from "../utils/fileDownload";
const filesize = require("file-size");

export const normalizeFileRecord = (file) => {
  if (!file || typeof file !== "object") return file;
  return {
    ...file,
    size: file.size ?? file.fileSize ?? 0,
    link: file.link ?? file.path ?? "",
    created: file.created ?? file.createAt,
    mimetype: file.mimetype ?? file.mimeType,
  };
};

const normalizeFileList = (files) =>
  (files || []).map((file) => normalizeFileRecord(file));

export const getFileUrl = (file) => {
  if (!file) return null;
  if (file.url) return file.url;
  if (file.link) {
    if (/^https?:\/\//i.test(file.link)) return file.link;
    return `${process.env.REACT_APP_FILE_URL || ""}${file.link}`;
  }
  return null;
};

const PdfPreviewer = ({ fileUrl, pageIndex = 1 }) => {
  const generateThumbnail = async (file) => {
    // console.log("generateThumbnail", file);
    try {
      const thumbnails = await generatePdfThumbnails(file, 150);
      // console.log(thumbnails);
    } catch (error) {
      console.log(error);
    }
    const thumbnails = generatePdfThumbnails(file, 150);
  };

  useEffect(() => {
    if (!fileUrl) return;
    generateThumbnail(fileUrl);
  }, [fileUrl]);
  return <>PDF</>;
};

export const isImageFile = (mimetype) => {
  return [
    "image/png",
    "image/gif",
    "image/jpg",
    "image/jpeg",
    "image/svg+xml",
    "image/webp",
    "video/mp4",
  ].includes(mimetype);
};

export const isPDFFile = (mimetype) => {
  return ["application/pdf"].includes(mimetype);
};

export const supportFiles = [
  { ext: "png", icon: faFileImage, color: "#8479F1", mimetype: "image/png" },
  { ext: "gif", icon: faFileImage, color: "#8479F1", mimetype: "image/gif" },
  {
    ext: "jpeg",
    icon: faFileImage,
    color: "#8479F1",
    mimetype: "image/jpeg",
  },
  { ext: "jpg", icon: faFileImage, color: "#8479F1", mimetype: "image/jpg" },
  { ext: "svg", icon: faFileImage, color: "#aaa", mimetype: "image/svg+xml" },
  { ext: "webp", icon: faFileImage, color: "#aaa", mimetype: "image/webp" },
  { ext: "mp4", icon: faFileVideo, color: "#aaa", mimetype: "video/mp4" },
  {
    ext: "zip",
    icon: faFileZipper,
    color: "#aaa",
    mimetype: "application/zip",
  },
  { ext: "csv", icon: faFileCsv, color: "#aaa", mimetype: "text/csv" },
  {
    ext: "pdf",
    icon: faFilePdf,
    color: "#EA5455",
    mimetype: "application/pdf",
  },
  {
    ext: "doc",
    icon: faFileWord,
    color: "#467FFF",
    mimetype: "application/msword",
  },
  {
    ext: "docx",
    icon: faFileWord,
    color: "#467FFF",
    mimetype:
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  },
  {
    ext: "xls",
    icon: faFileExcel,
    color: "green",
    mimetype: "application/vnd.ms-excel",
  },
  {
    ext: "xlsx",
    icon: faFileExcel,
    color: "green",
    mimetype:
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  },
  {
    ext: "ppt",
    icon: faFilePowerpoint,
    color: "#EA5455",
    mimetype: "application/vnd.ms-powerpoint",
  },
  {
    ext: "pptx",
    icon: faFilePowerpoint,
    color: "#EA5455",
    mimetype:
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  },
  {
    ext: "exe",
    icon: faFile,
    color: "black",
    mimetype: "application/vnd.microsoft.portable-executable",
  },
];

export const getFileIcon = (mimetype) => {
  let file = supportFiles.find((f) => f.mimetype === mimetype);
  if (!file) return faFile;
  return file.icon;
};

export const getFileIconColor = (mimetype) => {
  let file = supportFiles.find((f) => f.mimetype === mimetype);
  if (!file) return "#ccc";
  return file.color;
};

export const K_FILE_LIST_DISPLAY_MODE = {
  GRID: 1,
  LIST: 2,
};

const getFileRowKey = (file) => file?._id || file?.link || file?.name;

const ValidatedBadgeView = ({ textSize = "xs", t }) => (
  <Group spacing={4} noWrap sx={{ flexShrink: 0 }}>
    <IconCircleCheck color="#00b341" size={18} style={{ flexShrink: 0 }} />
    <Text size={textSize} color="green">
      {t("Validated")}
    </Text>
  </Group>
);

const FileListTableBody = ({
  tableFiles,
  textSize = "xs",
  t,
  enableValidate,
  allowEdit,
  disabled,
  handlersRef,
}) => {
  const [editingKey, setEditingKey] = useState(null);
  const [editingName, setEditingName] = useState("");

  const h = () => handlersRef.current || {};

  const setRowEdit = (row) => {
    setEditingKey(getFileRowKey(row));
    setEditingName(row.name ?? "");
  };

  const clearRowEdit = () => {
    setEditingKey(null);
    setEditingName("");
  };

  const getApiErrorMessage = (api, result, fallback = "Request failed") => {
    const fromError = api?.getErrorMsg?.(result?.error);
    if (fromError) return fromError;
    if (result?.error?.msg) return String(result.error.msg);
    if (result?.message && result.message !== "ERROR_POST_DATA") {
      return result.message;
    }
    return fallback;
  };

  const changeFileName = async (index) => {
    const handlers = h();
    const currentFiles = handlers.tableFiles;
    const originalFile = currentFiles?.[index];
    try {
      if (index === -1 || !originalFile) return;
      const r = { ...originalFile, name: editingName };
      const result = await handlers.api.renameFile(r._id, r.link, r.name);
      if (!result?.success) {
        throw new Error(
          getApiErrorMessage(
            handlers.api,
            result,
            "Rename failed — file may be missing on disk. Refresh and try again."
          )
        );
      }

      if (handlers.relatedObject?.entity === "fileGroup") {
        const rows = currentFiles.map((file, i) =>
          i === index
            ? {
                ...file,
                name: r.name,
                link: result.data.newLink,
                url: result.data.newUrl,
                filename: result.data.newName,
              }
            : file
        );

        const resultUpdate = await handlers.api.updateById({
          apiEntity: "fileGroup",
          id: handlers.relatedObject.id,
          values: { files: rows },
        });
        if (!resultUpdate?.success) {
          await handlers.api.renameFile(
            r._id,
            result.data.newLink,
            originalFile.name
          );
          throw new Error(
            getApiErrorMessage(handlers.api, resultUpdate, "Rename failed")
          );
        }
        const persistResult = await handlers.persistFiles(rows);
        if (persistResult?.success === false) {
          throw new Error(
            getApiErrorMessage(
              handlers.api,
              persistResult,
              "File renamed on disk but could not save to record"
            )
          );
        }
      } else if (result.success && result.data) {
        const rows = currentFiles.map((file, i) =>
          i === index
            ? {
                ...file,
                name: result.data.newName,
                link: result.data.newLink,
                url: result.data.newUrl,
              }
            : file
        );
        clearRowEdit();
        const persistResult = await handlers.persistFiles(rows);
        if (persistResult?.success === false) {
          await handlers.api.renameFile(
            r._id,
            result.data.newLink,
            originalFile.name
          );
          throw new Error(
            getApiErrorMessage(
              handlers.api,
              persistResult,
              "File renamed on disk but could not save to record"
            )
          );
        }
        if (handlers.relatedObject) {
          return await handlers.updateRelatedObject(rows);
        }
      }
    } catch (error) {
      console.log(error);
      showNotification({
        title: `Rename file ${originalFile?.name} unsuccessful`,
        color: "red",
        icon: <IconExclamationMark size={18} />,
        message: error.message || "Rename Fail",
      });
    } finally {
      clearRowEdit();
    }
  };

  return (
    <tbody>
      {tableFiles?.map((row, index) => {
        const fileUrl = h().getFileUrlForRow?.(row);
        const rowKey = getFileRowKey(row);
        const isEditing = editingKey === rowKey;
        return (
          <tr
            key={rowKey || index}
            onDoubleClick={(e) => {
              e.stopPropagation();
              if (fileUrl) window.open(fileUrl, "_blank", "noopener,noreferrer");
            }}
            style={{ cursor: fileUrl ? "pointer" : "default" }}
          >
            <td>
              <Checkbox
                checked={!!row.selected}
                onChange={(e) => {
                  h().onToggleSelected?.(row, e.target.checked);
                }}
              />
            </td>
            <td align="left" style={{ minWidth: 250 }}>
              {!isEditing && (
                <Group noWrap spacing={"xs"}>
                  <FontAwesomeIcon
                    icon={getFileIcon(row.mimetype)}
                    color={getFileIconColor(row.mimetype)}
                    size="xl"
                  />
                  <UnstyledButton
                    onClick={(e) => {
                      e.stopPropagation();
                      h().openFile?.(row);
                    }}
                  >
                    <Text size={textSize}> {row.name}</Text>
                  </UnstyledButton>
                  {enableValidate && row.validated && (
                    <ValidatedBadgeView textSize={textSize} t={t} />
                  )}
                </Group>
              )}
              {isEditing && (
                <TextInput
                  size="xs"
                  autoFocus
                  value={editingName}
                  onBlur={() => changeFileName(index)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      changeFileName(index);
                    }
                    if (e.key === "Escape") clearRowEdit();
                  }}
                  onChange={(e) => setEditingName(e.currentTarget.value)}
                />
              )}
            </td>
            <td>
              <Text size={textSize}>
                {filesize(row.size, { fixed: 0 }).human("si")}
              </Text>
            </td>
            <td>
              <Text size={textSize}>
                {row.created ? moment(row.created).format("LLL") : "--"}
              </Text>
            </td>
            <td>
              <Group noWrap spacing={1}>
                {fileUrl && (
                  <>
                    <ActionIcon
                      component="a"
                      href={fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={t("Open in new tab")}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <IconExternalLink size="1rem" />
                    </ActionIcon>
                    <ActionIcon
                      title={t("Download")}
                      onClick={(e) => {
                        e.stopPropagation();
                        h().downloadSingleFile?.(row);
                      }}
                    >
                      <IconDownload size="1rem" />
                    </ActionIcon>
                  </>
                )}
                {!disabled && allowEdit && (
                  <>
                    <ActionIcon
                      title={t("Rename")}
                      onClick={(e) => {
                        e.stopPropagation();
                        setRowEdit(row);
                      }}
                    >
                      <IconPencil size="1rem" />
                    </ActionIcon>
                    <ActionIcon
                      title={t("Delete")}
                      onClick={(e) => {
                        e.stopPropagation();
                        h().handleRemove?.(row);
                      }}
                    >
                      <IconTrash size="1rem" />
                    </ActionIcon>
                  </>
                )}
                {enableValidate && allowEdit && !disabled && (
                  <Menu withinPortal position="bottom-end">
                    <Menu.Target>
                      <ActionIcon
                        title={t("Actions")}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <IconDotsVertical size="1rem" />
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      {!row.validated ? (
                        <Menu.Item
                          icon={<IconCircleCheck size={14} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            h().setFileValidated?.(row, true);
                          }}
                        >
                          {t("Validate")}
                        </Menu.Item>
                      ) : (
                        <Menu.Item
                          icon={<IconCircleCheck size={14} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            h().setFileValidated?.(row, false);
                          }}
                        >
                          {t("Unvalidate")}
                        </Menu.Item>
                      )}
                    </Menu.Dropdown>
                  </Menu>
                )}
              </Group>
            </td>
          </tr>
        );
      })}
    </tbody>
  );
};

const FileList = ({
  form,
  name,
  relatedObject,
  staticFiles,
  skipInitialFetch = false,
  title,
  columnSpan = 4,
  disabled = false,
  uploadLabel = "Upload Files",
  tableMarginBottom = 100,
  showNoFilesMessage = true,
  defaultDisplayMode = K_FILE_LIST_DISPLAY_MODE.LIST,
  height = 600,
  showScroll = false,
  enableValidate = false,
  manageFilesLocally = false,
  sectionFiles,
  onFilesChange,
  onAutoSave,
  backupEntityType,
  backupEntityCodeField = "code",
  ...props
}) => {
  const forceUpdate = useForceUpdate();
  const [api] = useServerApi();
  const [uploading, setUploading] = useState(false);
  const [filesLoading, setFilesLoading] = useState(false);
  const [displayMode, setDisplayMode] = useState(defaultDisplayMode);
  const getLocalInitialFiles = () => {
    if (sectionFiles !== undefined) return normalizeFileList(sectionFiles);
    if (staticFiles !== undefined) return normalizeFileList(staticFiles);
    if (form && name) return normalizeFileList(_.get(form.values, name) || []);
    return [];
  };
  const [files, setFiles] = useState(() =>
    manageFilesLocally ? getLocalInitialFiles() : []
  );
  const [sortBy, setSortBy] = useState(null); // null, 'name', 'size', 'date'
  const [sortDirection, setSortDirection] = useState("asc"); // 'asc' or 'desc'
  const [searchText, setSearchText] = useState("");

  const resetRef = useRef(null);
  const tableHandlersRef = useRef({});

  const recordId = form?.values?._id;
  const reloadKey = form?._reloadKey ?? 0;
  const formFiles = form && name ? _.get(form.values, name) : undefined;

  const fetchFiles = async () => {
    try {
      if (!relatedObject) return;
      if (!relatedObject.entity || !relatedObject.id) return;
      setFilesLoading(true);
      let data = await api.getById({
        apiEntity: relatedObject?.entity,
        id: relatedObject?.id,
      });
      if (data) setFiles(data[relatedObject?.fieldName] || []);
    } catch (error) {
      console.log(error);
    } finally {
      setFilesLoading(false);
    }
  };

  useEffect(() => {
    if (
      !relatedObject &&
      !form &&
      !name &&
      staticFiles === undefined &&
      sectionFiles === undefined
    ) {
      return;
    }

    if (manageFilesLocally) {
      const source =
        sectionFiles !== undefined
          ? sectionFiles
          : staticFiles !== undefined
          ? staticFiles
          : formFiles || [];
      setFiles(normalizeFileList(source));
      return;
    }

    if (staticFiles !== undefined) {
      setFiles(normalizeFileList(staticFiles));
    }
    if (form && name) {
      return setFiles(
        normalizeFileList(formFiles || staticFiles || [])
      );
    }

    if (relatedObject && !skipInitialFetch) {
      fetchFiles();
    }
  }, [
    manageFilesLocally,
    recordId,
    reloadKey,
    staticFiles,
    relatedObject,
    skipInitialFetch,
    ...(manageFilesLocally ? [] : [formFiles, name, sectionFiles]),
  ]);

  const persistFormFiles = async (updatedFiles) => {
    if (onFilesChange) {
      return onFilesChange(updatedFiles, form);
    }
    if (!form || !name) return;
    form.setFieldValue(name, updatedFiles);

    if (typeof onAutoSave === "function") {
      return onAutoSave(updatedFiles, form);
    }

    const recordExists = Boolean(_.get(form?.values, "_id"));
    if (recordExists && typeof form.savePartialFields === "function") {
      return form.savePartialFields({ [name]: updatedFiles });
    }
    if (recordExists) {
      return form.updateAndSave({ [name]: updatedFiles });
    }
  };

  const updateRelatedObject = async (newFiles) => {
    try {
      const fieldName = relatedObject?.fieldName;
      if (!fieldName) {
        return { success: false, error: { msg: "Missing related object field" } };
      }
      const result = await api.updateById({
        apiEntity: relatedObject?.entity,
        id: relatedObject?.id,
        values: { [fieldName]: newFiles },
      });
      if (!result?.success) {
        return {
          success: false,
          error: result?.error,
          message: result?.message,
        };
      }
      setFiles(newFiles);
      return result;

      // relatedObject.onUpdate &&
      //   relatedObject.onUpdate(data[relatedObject?.fieldName]);
    } catch (error) {
      console.log(error);
      return { success: false, error: { msg: error.message } };
    }
  };

  const entityId = relatedObject?.id || _.get(form?.values, "_id");
  const NO_FILES_MESSAGE = "No files upload yet.";
  const auth = useAuthUser();
  const { t } = useTranslation();
  const role = auth()?.userRole?.name?.toUpperCase() ?? "CONSULTANT";

  const [allowEdit, setAllowEdit] = useState(false);

  useEffect(() => {
    if (role === "CONSULTANT") return setAllowEdit(false);
    setAllowEdit(true);
  }, [role]);

  const getFileKey = (file) => file?._id || file?.link || file?.name;

  const applyValidationToFile = (file, validated) => {
    if (validated) {
      file.validated = true;
      file.validatedAt = new Date();
      file.validatedBy = auth()?.id;
    } else {
      delete file.validated;
      delete file.validatedAt;
      delete file.validatedBy;
    }
  };

  const persistFiles = async (updatedFiles) => {
    setFiles(updatedFiles);
    if (onFilesChange) {
      onFilesChange(updatedFiles, form);
      return;
    }
    if (form && name) {
      await persistFormFiles(updatedFiles);
    } else if (relatedObject) {
      await updateRelatedObject(updatedFiles);
    }
  };

  const setFileValidated = (row, validated) => {
    const rowKey = getFileKey(row);
    const updatedFiles = files.map((f) => {
      if (getFileKey(f) !== rowKey) return f;
      const clone = { ...f };
      applyValidationToFile(clone, validated);
      return clone;
    });
    persistFiles(updatedFiles);
  };

  const setSelectedFilesValidated = async (validated) => {
    const selected = getSelectedFiles();
    if (!selected.length) return;
    const selectedKeys = new Set(selected.map(getFileKey));
    const updatedFiles = files.map((f) => {
      if (!selectedKeys.has(getFileKey(f))) return f;
      const clone = { ...f };
      applyValidationToFile(clone, validated);
      return clone;
    });
    await persistFiles(updatedFiles);
  };

  const ValidatedBadge = ({ textSize = "xs" }) => (
    <Group spacing={4} noWrap sx={{ flexShrink: 0 }}>
      <IconCircleCheck color="#00b341" size={18} style={{ flexShrink: 0 }} />
      <Text size={textSize} color="green">
        {t("Validated")}
      </Text>
    </Group>
  );

  useHotkeys([
    ["ctrl+A", () => selectAll()],
    ["mod+A", () => selectAll()],
    ["backspace", () => deleteSelected()],
    ["delete", () => deleteSelected()],
    ["escape", () => unSelectAll()],
  ]);

  const resetFileButton = () => resetRef.current?.();

  const selectAll = () => {
    files.forEach((f) => (f.selected = true));
    forceUpdate();
  };

  const unSelectAll = () => {
    // console.log("unSelectAll");
    files.forEach((f) => (f.selected = false));
    forceUpdate();
  };

  const getSelectedFiles = () => files.filter((f) => f.selected);

  const deleteSelected = () => {
    if (disabled) return;
    if (!window.confirm(t("Are you sure to remove the files?"))) return;
    handleRemoveSelected();
  };

  const downloadFileRecords = async (fileRecords, filename = "files.zip") => {
    if (!fileRecords?.length) return;
    try {
      const result = await api.downloadFiles(fileRecords, filename);
      saveBlobDownload(result.blob, result.filename);
    } catch (error) {
      console.log(error);
      showNotification({
        title: t("Download failed"),
        color: "red",
        icon: <IconExclamationMark size={18} />,
        message: error?.message || "Download Fail",
      });
    }
  };

  const downloadSelected = () =>
    downloadFileRecords(getSelectedFiles(), "selected-files.zip");

  const downloadAll = () => downloadFileRecords(files, "all-files.zip");

  const downloadSingleFile = (file) =>
    downloadFileRecords([file], file.name || file.filename || "download");

  const getFileUrlForRow = (file) => {
    if (relatedObject?.entity === "fileGroup" && file?.url) {
      return file.url;
    }
    return getFileUrl(file);
  };

  const handleUploadFiles = async (uploadFiles) => {
    try {
      console.log("handleUploadFiles", uploadFiles);

      let newFiles = [];
      for (const file of uploadFiles) {
        if (!file) continue;

        setUploading(true);

        // Handle fileGroup uploads differently
        if (relatedObject?.entity === "fileGroup") {
          console.log("handleUploadFiles - fileGroup", relatedObject);
          let result = await api.FileGroup.uploadFile(relatedObject.id, file);
          if (result && result.success) {
            showNotification({
              title: `Upload successfully`,
              message: "Upload Success",
            });
            newFiles.push(result.data);
          } else {
            showNotification({
              title: `${file.name} upload unsuccessful`,
              color: "red",
              icon: <IconExclamationMark size={18} />,
              message: result?.message || "Upload Fail",
            });
          }
        } else {
          // Original upload logic for other entities
          let result = await api.uploadFile("files", file, entityId, files, {
            entityType: backupEntityType || relatedObject?.entity || "",
            entityField: name || relatedObject?.fieldName || "",
            entityCode:
              _.get(form?.values, backupEntityCodeField) ||
              relatedObject?.entityCode ||
              "",
          });
          let { data, success } = result;
          if (success) {
            showNotification({
              title: `Upload successfully`,
              message: "Upload Success",
            });
            newFiles.push(data);
          } else {
            showNotification({
              title: `${file.name} upload unsuccessful`,
              color: "red",
              icon: <IconExclamationMark size={18} />,
              message: "Upload Fail",
            });
          }
        }

        setUploading(false);
      }

      resetFileButton();
      if (form && name) return persistFiles([...files, ...newFiles]);

      if (relatedObject) {
        if (relatedObject.entity === "fileGroup") {
          // For fileGroup, refresh the files by fetching the updated folder
          await fetchFiles();
        } else {
          return await updateRelatedObject([...files, ...newFiles]);
        }
      }
    } catch (error) {
      console.log(error);
      setUploading(false);
      showNotification({
        title: t(`Files upload unsuccessful`),
        color: "red",
        icon: <IconExclamationMark size={18} />,
        message: t("Upload Fail"),
      });
    }
  };

  const handleRemoveSelected = async () => {
    const selectedFiles = getSelectedFiles();

    //remove from files
    const newFiles = files.filter((f) => !selectedFiles.includes(f));
    if (form && name) {
      await persistFiles(newFiles);
    } else {
      setFiles(newFiles);
    }
    if (relatedObject) return await updateRelatedObject(newFiles);

    unSelectAll();
    forceUpdate();
  };

  const handleRemove = async (file, needConfirm = true) => {
    if (
      needConfirm &&
      !window.confirm(t(`Are you sure to remove the file ${file.name}?`))
    )
      return;

    // Handle fileGroup file removal
    if (relatedObject?.entity === "fileGroup") {
      try {
        const result = await api.FileGroup.removeFile(
          relatedObject.id,
          file.name
        );
        if (result && result.success) {
          showNotification({
            title: "Success",
            message: "File removed successfully",
            color: "green",
            icon: <IconCheck size={18} />,
          });
          // Refresh files
          await fetchFiles();
        } else {
          showNotification({
            title: "Error",
            message: result?.message || "Failed to remove file",
            color: "red",
            icon: <IconExclamationMark size={18} />,
          });
        }
        return;
      } catch (error) {
        console.error("Error removing file:", error);
        showNotification({
          title: "Error",
          message: "Failed to remove file",
          color: "red",
          icon: <IconExclamationMark size={18} />,
        });
        return;
      }
    }

    // Original removal logic for other entities
    let index = -1;
    if (file._id) {
      index = files.findIndex((f) => f._id === file._id);
      if (index === -1) return;
    } else if (file.name) {
      index = files.findIndex((f) => f.name === file.name);
      if (index === -1) return;
    }

    const newFiles = [...files.slice(0, index), ...files.slice(index + 1)];
    if (form && name) return persistFiles(newFiles);
    setFiles(newFiles);
    if (relatedObject) {
      return await updateRelatedObject(newFiles);
    }
  };

  const handleEditFileName = (file, editing) => {
    file.editing = editing;
    forceUpdate();
  };

  const setSelected = (file) => {
    if (file.selected) return openFile(file);
    unSelectAll();
    file.selected = true;
    forceUpdate();
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // Set new column and default to ascending
      setSortBy(column);
      setSortDirection("asc");
    }
  };

  const getFilteredFiles = (filesToFilter) => {
    const q = searchText.trim().toLowerCase();
    if (!q) return filesToFilter;
    return filesToFilter.filter((f) => {
      const name = (f.name || f.filename || "").toLowerCase();
      return name.includes(q);
    });
  };

  const getSortedFiles = (filesToSort) => {
    if (!sortBy) return filesToSort;

    const sorted = [...filesToSort].sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case "name":
          aValue = (a.name || "").toLowerCase();
          bValue = (b.name || "").toLowerCase();
          break;
        case "size":
          aValue = a.size || 0;
          bValue = b.size || 0;
          break;
        case "date":
          aValue = new Date(a.created || 0).getTime();
          bValue = new Date(b.created || 0).getTime();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) return <IconSelector size={14} />;
    return sortDirection === "asc" ? (
      <IconChevronUp size={14} />
    ) : (
      <IconChevronDown size={14} />
    );
  };

  const FilesActionButton = () => (
    <Menu shadow="md" width={200}>
      <Menu.Target>
        <ActionIcon variant="default">
          <IconDotsVertical size="1rem" />
        </ActionIcon>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Label>{`Actions`}</Menu.Label>
        <Menu.Item onClick={downloadSelected} icon={<IconDownload size={14} />}>
          Download Selected
        </Menu.Item>
        <Menu.Item onClick={downloadAll} icon={<IconDownload size={14} />}>
          Download All
        </Menu.Item>
        <Menu.Item
          disabled={disabled}
          onClick={deleteSelected}
          icon={<IconTrash size={14} />}
        >
          Delete
        </Menu.Item>
        {enableValidate && allowEdit && !disabled && (
          <>
            <Menu.Divider />
            <Menu.Item
              disabled={getSelectedFiles().length === 0}
              onClick={() => setSelectedFilesValidated(true)}
              icon={<IconCircleCheck size={14} />}
            >
              {t("Validate Selected")}
            </Menu.Item>
            <Menu.Item
              disabled={getSelectedFiles().length === 0}
              onClick={() => setSelectedFilesValidated(false)}
              icon={<IconCircleCheck size={14} />}
            >
              {t("Unvalidate Selected")}
            </Menu.Item>
          </>
        )}
      </Menu.Dropdown>
    </Menu>
  );

  const openFile = (file) => {
    const url = getFileUrlForRow(file);
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleCardOnClick = (event, file) => {
    if (event.altKey) {
      // console.log("Alt, yay!");
      file.selected = true;
      return forceUpdate();
    }
    setSelected(file);
  };

  const Previewer = ({ file }) => {
    const isImage = isImageFile(file.mimetype);
    const isPdf = isPDFFile(file.mimetype);

    return (
      <>
        {isImage && (
          <div style={{ height: "140px", bakcgorund: "#F8F8F8" }}>
            <Image
              src={
                relatedObject?.entity === "fileGroup"
                  ? file.url || `${process.env.REACT_APP_FILE_URL}${file.link}`
                  : `${process.env.REACT_APP_FILE_URL}${file.link}`
              }
              height={100}
            />

            {/* {`${process.env.REACT_APP_FILE_URL}${file.link}`} */}
          </div>
        )}

        {/* {isPdf && file && file.url && <PdfPreviewer fileUrl={file.url} />} */}

        {!isImage && (
          <Center style={{ height: "140px", background: "#F8F8F8" }}>
            <FontAwesomeIcon
              icon={getFileIcon(file.mimetype)}
              color={getFileIconColor(file.mimetype)}
              size="xl"
            ></FontAwesomeIcon>
          </Center>
        )}
      </>
    );
  };

  const FileCard = ({ file }) => (
    <Card
      withBorder
      shadow="sm"
      radius="md"
      style={{ borderColor: file.selected ? "#A5D8FF" : "" }}
      onClick={(e) => {
        handleCardOnClick(e, file);
      }}
    >
      <Card.Section>
        <Previewer file={file} />
      </Card.Section>
      <Group mt="sm" spacing="xs" noWrap>
        <Text size="sm">{file.name ?? file.filename}</Text>
        {enableValidate && file.validated && (
          <ValidatedBadge textSize="sm" />
        )}
      </Group>
      <Group position="apart">
        <Text color="dimmed" size="xs">
          {moment(file.created).format("YYYY/MM/DD")}
        </Text>
        <Text mt="sm" color="dimmed" size="xs">
          {filesize(file.size, { fixed: 0 }).human("si")}
        </Text>
      </Group>
    </Card>
  );

  const FilesGrid = ({ files }) => (
    <Grid mb={50}>
      {files &&
        files?.map((file) => (
          <Grid.Col key={file._id || file.id} span={columnSpan}>
            <div style={{ zIndex: 200 }}>
              <FileCard file={file} />
            </div>
          </Grid.Col>
        ))}
      <Center w={300} h={200}>
        {uploading && <Loader variant="bars" />}
      </Center>
    </Grid>
  );

  const displayFiles = getSortedFiles(getFilteredFiles(files));
  const allSelected =
    displayFiles.length > 0 && displayFiles.every((f) => f.selected);

  tableHandlersRef.current = {
    api,
    relatedObject,
    persistFiles,
    updateRelatedObject,
    tableFiles: displayFiles,
    getFileUrlForRow,
    openFile,
    downloadSingleFile,
    handleRemove,
    setFileValidated,
    onToggleSelected: (row, checked) => {
      row.selected = checked;
      forceUpdate();
    },
  };

  const renderListTable = () => (
    <Table mb={tableMarginBottom} striped highlightOnHover>
      <thead>
        <tr>
          <th>
            <Checkbox
              checked={allSelected}
              onChange={(event) => {
                displayFiles.forEach(
                  (f) => (f.selected = event.target.checked)
                );
                forceUpdate();
              }}
            />
          </th>
          <th>
            <UnstyledButton onClick={() => handleSort("name")}>
              <Group spacing={5}>
                <Text size="xs">File</Text>
                {getSortIcon("name")}
              </Group>
            </UnstyledButton>
          </th>
          <th>
            <UnstyledButton onClick={() => handleSort("size")}>
              <Group spacing={5}>
                <Text size="xs">Size</Text>
                {getSortIcon("size")}
              </Group>
            </UnstyledButton>
          </th>
          <th>
            <UnstyledButton onClick={() => handleSort("date")}>
              <Group spacing={5}>
                <Text size="xs">Date</Text>
                {getSortIcon("date")}
              </Group>
            </UnstyledButton>
          </th>
          <th>
            <Text size="xs">{t("Action")}</Text>
          </th>
        </tr>
      </thead>
      <FileListTableBody
        tableFiles={displayFiles}
        t={t}
        enableValidate={enableValidate}
        allowEdit={allowEdit}
        disabled={disabled}
        handlersRef={tableHandlersRef}
      />
    </Table>
  );

  const renderFileContent = () => {
    if (displayFiles.length === 0) {
      return (
        <Text size="xs" color="dimmed" my="md">
          {t("No matches")}
        </Text>
      );
    }
    if (displayMode === K_FILE_LIST_DISPLAY_MODE.GRID) {
      return <FilesGrid files={displayFiles} />;
    }
    return renderListTable();
  };

  const renderFiles = (mt = 10, mb = 20) => (
    <>
      <Group position="apart" mb={mb} mt={mt} noWrap>
        <TextInput
          size="xs"
          placeholder={t("Search")}
          icon={<IconSearch size={14} stroke={1.5} />}
          value={searchText}
          onChange={(e) => setSearchText(e.currentTarget.value)}
          style={{ flex: 1, maxWidth: 360 }}
          rightSection={
            searchText ? (
              <UnstyledButton
                onClick={() => setSearchText("")}
                aria-label={t("Clear")}
              >
                <IconX size={14} stroke={1.5} />
              </UnstyledButton>
            ) : null
          }
        />
        <Group spacing="xs">
          <ActionIcon
            variant="default"
            onClick={() => {
              if (form) form?.reload();
              if (relatedObject) {
                fetchFiles();
              }
            }}
          >
            <IconRefresh size="1rem" />
          </ActionIcon>
          <ActionIcon
            variant="default"
            onClick={() => setDisplayMode(K_FILE_LIST_DISPLAY_MODE.LIST)}
          >
            <IconList size="1rem" />
          </ActionIcon>
          <ActionIcon
            variant="default"
            onClick={() => setDisplayMode(K_FILE_LIST_DISPLAY_MODE.GRID)}
          >
            <IconLayoutGrid size="1rem" />
          </ActionIcon>
          <FilesActionButton />
        </Group>
      </Group>
      {showScroll && (
        <ScrollArea h={height} type="always">
          {renderFileContent()}
        </ScrollArea>
      )}
      {!showScroll && renderFileContent()}
    </>
  );

  return (
    <Box>
      {/* FileList */}
      {/* <ReactJson
        src={form.values}
        name="files"
        style={{ background: "white" }}
        collapsed
      /> */}
      {/* entityId:{entityId} */}
      {/* <ReactJson src={files.filter(f =>f.selected)} name="files" style={{background:"white"}} collapsed/> */}
      {title && <Input.Label> {title}</Input.Label>}
      {!disabled && (
        <Dropzone
          onDrop={handleUploadFiles}
          onReject={(files) => console.log("rejected files", files)}
          {...props}
        >
          <FileButton resetRef={resetRef} onChange={handleUploadFiles} multiple>
            {(props) => (
              <Button {...props} size="xs" loading={uploading}>
                {t(uploadLabel)}
              </Button>
            )}
          </FileButton>
        </Dropzone>
      )}
      {filesLoading && (
        <Center my="xl">
          <Loader variant="bars" />
        </Center>
      )}
      {!filesLoading && !_.isEmpty(files) && renderFiles()}
      {!filesLoading && showNoFilesMessage && _.isEmpty(files) && (
        <Text size="xs" my={"xl"}>
          {t(NO_FILES_MESSAGE)}
        </Text>
      )}
    </Box>
  );
};

export default FileList;
